import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// Garden gnome — a jolly little lawn guardian: a tall pointy red hat, a big
// cream beard, a round blue belly with a belt, stubby arms and chunky boots.
// Built chunky and toy-like (about two heads tall, big feet), roughly 0.6 m.
//
// Frame: faces +Z (where the nose points), sits on the floor (y = 0).
//
// Rig layout (flat, no hierarchy — each named node moves about its own pivot):
//   - "body" : the belly, belt and arms. Pivot at the boot tops so it rocks
//              and squashes in place.
//   - "head" : the face, beard, brows and nose. Pivot at the collar so nods
//              read as a head, not a floating ball.
//   - "hat"  : the pointy hat. Pivot at its brim so it wobbles and lifts on
//              the signature jolly bounce.
// The boots stay planted (unnamed, merged into the static mesh).

const HAT = "#d9483b";         // signature gnome red
const HAT_DARK = "#b93a2f";    // shaded hat roll
const TUNIC = "#4a7fc1";       // friendly blue tunic
const BELT = "#8a5a33";        // leather belt
const BOOT = "#6d4a2a";        // chunky boots
const SKIN = "#f2b98d";        // warm rosy skin
const NOSE = "#eba06e";        // a deeper blush for the big nose
const CHEEK = "#e89a72";       // rosy cheeks
const BEARD = "#f6efe3";       // cream beard, brows and buckle
const COAL = "#2c2f36";        // deep charcoal pupils
const SPARK = "#fdfbf6";       // eye highlights

// ── frame heights ────────────────────────────────────────────────────────────
const BOOT_TOP = 0.09;         // the body rocks about the boot tops
const BELLY_CY = 0.23;         // belly ball centre (r 0.155)
const COLLAR_Y = 0.34;         // the head pivots here
const HEAD_CY = 0.42;          // head ball centre (r 0.105)
const BRIM_Y = 0.478;          // hat roll — the hat pivots here

const BODY_PIVOT: [number, number, number] = [0, BOOT_TOP, 0];
const HEAD_PIVOT: [number, number, number] = [0, COLLAR_Y, 0];
const HAT_PIVOT: [number, number, number] = [0, BRIM_Y, 0];

// One stubby arm: a short tunic sleeve angled down-and-out plus a bare hand.
function stubbyArm(side: number): Part[] {
  return [
    { geometry: new THREE.CapsuleGeometry(0.038, 0.10, 4, 10), color: TUNIC,
      position: [side * 0.15, 0.235, 0.02], rotation: [0, 0, side * 0.55], name: "body" },
    { geometry: new THREE.SphereGeometry(0.042, 10, 8), color: SKIN,
      position: [side * 0.185, 0.165, 0.03], name: "body" },
  ];
}

function gnomeParts(): Part[] {
  return [
    // ── boots (static) — big rounded toes pointing +Z ───────────────────────
    { geometry: new THREE.SphereGeometry(0.055, 12, 10), color: BOOT,
      position: [-0.07, 0.045, 0.025], scale: [1.05, 0.75, 1.5] },
    { geometry: new THREE.SphereGeometry(0.055, 12, 10), color: BOOT,
      position: [0.07, 0.045, 0.025], scale: [1.05, 0.75, 1.5] },

    // ── body: round belly, belt and arms ────────────────────────────────────
    { geometry: new THREE.SphereGeometry(0.155, 18, 14), color: TUNIC,
      position: [0, BELLY_CY, 0], scale: [1, 0.95, 0.9], name: "body" },
    // Leather belt with a little cream buckle.
    { geometry: new THREE.CylinderGeometry(0.15, 0.152, 0.045, 16), color: BELT,
      position: [0, 0.185, 0], name: "body" },
    { geometry: new THREE.BoxGeometry(0.05, 0.035, 0.02), color: BEARD,
      position: [0, 0.185, 0.153], name: "body" },
    ...stubbyArm(-1),
    ...stubbyArm(1),

    // ── head: face, nose, eyes, cheeks, brows, beard ────────────────────────
    { geometry: new THREE.SphereGeometry(0.105, 18, 14), color: SKIN,
      position: [0, HEAD_CY, 0], name: "head" },
    // The big friendly nose.
    { geometry: new THREE.SphereGeometry(0.034, 10, 8), color: NOSE,
      position: [0, 0.418, 0.105], name: "head" },
    // Big eyes: cream whites, coal pupils set forward, tiny sparkles.
    { geometry: new THREE.SphereGeometry(0.026, 10, 8), color: BEARD,
      position: [-0.048, 0.45, 0.082], name: "head" },
    { geometry: new THREE.SphereGeometry(0.026, 10, 8), color: BEARD,
      position: [0.048, 0.45, 0.082], name: "head" },
    { geometry: new THREE.SphereGeometry(0.017, 8, 6), color: COAL,
      position: [-0.046, 0.452, 0.100], name: "head" },
    { geometry: new THREE.SphereGeometry(0.017, 8, 6), color: COAL,
      position: [0.046, 0.452, 0.100], name: "head" },
    { geometry: new THREE.SphereGeometry(0.006, 6, 5), color: SPARK,
      position: [-0.040, 0.458, 0.113], name: "head" },
    { geometry: new THREE.SphereGeometry(0.006, 6, 5), color: SPARK,
      position: [0.052, 0.458, 0.113], name: "head" },
    // Rosy cheeks.
    { geometry: new THREE.SphereGeometry(0.018, 8, 6), color: CHEEK,
      position: [-0.075, 0.405, 0.065], name: "head" },
    { geometry: new THREE.SphereGeometry(0.018, 8, 6), color: CHEEK,
      position: [0.075, 0.405, 0.065], name: "head" },
    // The big beard: a soft mass under the chin, spilling onto the chest.
    { geometry: new THREE.SphereGeometry(0.095, 14, 12), color: BEARD,
      position: [0, 0.35, 0.048], scale: [1.05, 0.9, 0.75], name: "head" },
    // Moustache halves flanking the nose.
    { geometry: new THREE.SphereGeometry(0.032, 10, 8), color: BEARD,
      position: [-0.032, 0.392, 0.095], scale: [1.2, 0.6, 0.6], rotation: [0, 0, 0.25], name: "head" },
    { geometry: new THREE.SphereGeometry(0.032, 10, 8), color: BEARD,
      position: [0.032, 0.392, 0.095], scale: [1.2, 0.6, 0.6], rotation: [0, 0, -0.25], name: "head" },

    // ── the tall pointy hat ─────────────────────────────────────────────────
    // Rolled brim seated on the head, then the cone with a happy backward tilt.
    { geometry: new THREE.CylinderGeometry(0.118, 0.122, 0.04, 16), color: HAT_DARK,
      position: [0, BRIM_Y, 0], name: "hat" },
    { geometry: new THREE.ConeGeometry(0.112, 0.21, 14), color: HAT,
      position: [0, BRIM_Y + 0.115, -0.008], rotation: [-0.09, 0, 0], name: "hat" },
  ];
}

export const gardenGnome = defineModel({
  id: "garden-gnome",
  batchId: "mess",
  name: "Garden Gnome",
  tags: ["mess", "cute", "animated", "character", "gnome", "garden", "mascot"],
  summary: "A jolly garden gnome with a tall red hat, a big cream beard and a bouncy greeting.",
  description:
    "A jolly little garden gnome: a round blue belly with a leather belt, " +
    "stubby arms, chunky boots, a big cream beard under rosy cheeks and a tall " +
    "pointy red hat. The body, head and hat are separately posable; it ships an " +
    "idle breathing bob and a jolly bounce where the hat wobbles and lifts.",
  author: "models",
  footprint: 0.3,
  tiles: { w: 1, h: 1 },
  parts: gnomeParts,
  animations: [
    // 1. Idle — a slow, contented breath: the belly swells, the head lifts a
    //    touch and the hat rides along with a tiny lag.
    {
      id: "idle",
      label: "Idle breathing",
      duration: 3.0,
      loop: true,
      tracks: [
        {
          target: "body", pivot: BODY_PIVOT,
          scale: [
            { t: 0, value: [1, 1, 1], ease: "sine" },
            { t: 1.5, value: [1.025, 0.985, 1.025], ease: "sine" },
            { t: 3.0, value: [1, 1, 1], ease: "sine" },
          ],
        },
        {
          target: "head", pivot: HEAD_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.5, value: [0, 0.012, 0], ease: "sine" },
            { t: 3.0, value: [0, 0, 0], ease: "sine" },
          ],
          rotation: [
            { t: 0, value: [0.025, 0, 0], ease: "sine" },
            { t: 1.5, value: [-0.025, 0, 0], ease: "sine" },
            { t: 3.0, value: [0.025, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "hat", pivot: HAT_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.7, value: [0, 0.014, 0], ease: "sine" },
            { t: 3.0, value: [0, 0, 0], ease: "sine" },
          ],
        },
      ],
    },

    // 2. Bounce — the signature jolly hop: a squash, a leap, a landing wobble,
    //    with the hat lagging behind and wobbling side to side on the way.
    {
      id: "bounce",
      label: "Jolly bounce",
      duration: 1.5,
      loop: true,
      tracks: [
        {
          target: "body", pivot: BODY_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "easeIn" },
            { t: 0.2, value: [0, -0.015, 0], ease: "easeOut" },
            { t: 0.5, value: [0, 0.07, 0], ease: "easeOut" },
            { t: 0.8, value: [0, 0, 0], ease: "easeIn" },
            { t: 0.95, value: [0, -0.012, 0], ease: "easeOut" },
            { t: 1.15, value: [0, 0, 0], ease: "easeOut" },
            { t: 1.5, value: [0, 0, 0] },
          ],
          scale: [
            { t: 0, value: [1, 1, 1], ease: "easeIn" },
            { t: 0.2, value: [1.07, 0.9, 1.07], ease: "easeOut" },
            { t: 0.5, value: [0.95, 1.08, 0.95], ease: "easeOut" },
            { t: 0.8, value: [1.06, 0.92, 1.06], ease: "easeOut" },
            { t: 1.15, value: [1, 1, 1], ease: "easeOut" },
            { t: 1.5, value: [1, 1, 1] },
          ],
        },
        // The head rides the hop with a touch of delay and a happy tip back.
        {
          target: "head", pivot: HEAD_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "easeIn" },
            { t: 0.25, value: [0, -0.015, 0], ease: "easeOut" },
            { t: 0.55, value: [0, 0.072, 0], ease: "easeOut" },
            { t: 0.85, value: [0, 0, 0], ease: "easeIn" },
            { t: 1.05, value: [0, -0.01, 0], ease: "easeOut" },
            { t: 1.25, value: [0, 0, 0], ease: "easeOut" },
            { t: 1.5, value: [0, 0, 0] },
          ],
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeOut" },
            { t: 0.5, value: [-0.14, 0, 0], ease: "easeOut" },
            { t: 0.85, value: [0.08, 0, 0], ease: "easeIn" },
            { t: 1.2, value: [0, 0, 0], ease: "easeOut" },
            { t: 1.5, value: [0, 0, 0] },
          ],
        },
        // The hat lags further, floats a little free at the top of the hop and
        // wobbles side to side as it settles — the jolly signature.
        {
          target: "hat", pivot: HAT_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "easeIn" },
            { t: 0.3, value: [0, -0.012, 0], ease: "easeOut" },
            { t: 0.6, value: [0, 0.088, 0], ease: "easeOut" },
            { t: 0.9, value: [0, 0.005, 0], ease: "easeIn" },
            { t: 1.1, value: [0, -0.008, 0], ease: "easeOut" },
            { t: 1.3, value: [0, 0, 0], ease: "easeOut" },
            { t: 1.5, value: [0, 0, 0] },
          ],
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeOut" },
            { t: 0.45, value: [0, 0, -0.16], ease: "easeInOut" },
            { t: 0.75, value: [0, 0, 0.18], ease: "easeInOut" },
            { t: 1.0, value: [0, 0, -0.1], ease: "easeInOut" },
            { t: 1.25, value: [0, 0, 0.04], ease: "easeInOut" },
            { t: 1.5, value: [0, 0, 0], ease: "easeOut" },
          ],
        },
      ],
    },
  ],
});
